import React, { useState } from "react";
import { useCart } from "../Cart";
import {
  Trash2,
  PlusCircle,
  MinusCircle,
  ShoppingBag,
  ArrowLeft,
  AlertTriangle,
} from "react-feather";
import { motion } from "framer-motion";
import { useNavigate } from "react-router-dom";
import { getProductImage } from "../utils/categoryUtils";

const ViewCart = () => {
  const { cartItems, removeFromCart, clearCart, updateCartItemQuantity } =
    useCart();
  const navigate = useNavigate();
  const [showPrescriptionModal, setShowPrescriptionModal] = useState(false);

  const totalCost = cartItems.reduce((total, item) => {
    const price = item.retailPrice ? parseFloat(item.retailPrice) : 0;
    return total + price * item.quantity;
  }, 0);

  // Check if cart has prescription items
  const hasPrescriptionItems = cartItems.some(
    (item) =>
      item.Prescription === "TRUE" ||
      item.Prescription === true ||
      item.Prescription === 1
  );

  const handleCheckout = () => {
    if (cartItems.length === 0) return;

    if (hasPrescriptionItems) {
      setShowPrescriptionModal(true);
      return;
    }

    proceedToCheckout();
  };

  const proceedToCheckout = () => {
    const message = cartItems
      .map(
        (item) =>
          `${item.quantity} x ${item.itemName} at Ksh ${item.retailPrice}`
      )
      .join(", ");
    const whatsappUrl = `https://wa.me/254101757541?text=I am interested in: ${encodeURIComponent(
      message
    )}. Total: Ksh ${totalCost.toFixed(2)}`;
    window.open(whatsappUrl, "_blank");
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-neutral-50 to-primary-50">
      <div className="container mx-auto px-4 py-16">
        {/* Header */}
        <div className="flex items-center justify-between mb-12">
          <motion.button
            onClick={() => navigate("/shop")}
            className="flex items-center space-x-2 text-neutral-600 hover:text-primary-600 transition-colors duration-300"
            whileHover={{ x: -5 }}
          >
            <ArrowLeft className="w-5 h-5" />
            <span>Continue Shopping</span>
          </motion.button>
          <div className="flex items-center space-x-3">
            <ShoppingBag className="w-8 h-8 text-primary-600" />
            <h1 className="text-3xl md:text-4xl font-bold text-neutral-900">
              Shopping Cart
            </h1>
          </div>
        </div>

        {cartItems.length === 0 ? (
          <motion.div
            className="text-center py-16"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <div className="w-32 h-32 bg-neutral-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <ShoppingBag className="w-16 h-16 text-neutral-400" />
            </div>
            <h2 className="text-2xl font-bold text-neutral-700 mb-4">
              Your cart is empty
            </h2>
            <p className="text-neutral-500 mb-8 max-w-md mx-auto">
              Looks like you haven't added any items to your cart yet. Start
              shopping to fill it up!
            </p>
            <motion.button
              onClick={() => navigate("/shop")}
              className="px-8 py-4 bg-gradient-to-r from-primary-600 to-primary-700 text-white rounded-xl font-semibold shadow-medium hover:shadow-large transition-all duration-300"
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
            >
              Start Shopping
            </motion.button>
          </motion.div>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            <div className="lg:col-span-2 space-y-6">
              {cartItems.map((item, index) => (
                <motion.div
                  key={index}
                  className="bg-white rounded-2xl shadow-soft border border-neutral-100 overflow-hidden hover:shadow-medium transition-all duration-300"
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.3, delay: index * 0.1 }}
                >
                  <div className="p-6">
                    <div className="flex items-center space-x-6">
                      <div className="relative">
                        <img
                          src={getProductImage(
                            item,
                            "https://images.unsplash.com/photo-1471864190281-a93a3070b6de?q=80&w=300"
                          )}
                          alt={item.itemName}
                          className="w-24 h-24 rounded-xl object-cover"
                        />
                        {(item.Prescription === "TRUE" ||
                          item.Prescription === true ||
                          item.Prescription === 1) && (
                          <div className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-1">
                            <AlertTriangle className="w-3 h-3" />
                          </div>
                        )}
                      </div>

                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-2">
                          <h3 className="text-lg font-bold text-neutral-900">
                            {item.itemName}
                          </h3>
                          {(item.Prescription === "TRUE" ||
                            item.Prescription === true ||
                            item.Prescription === 1) && (
                            <span className="bg-red-100 text-red-700 px-2 py-1 rounded-full text-xs font-medium">
                              Prescription Required
                            </span>
                          )}
                        </div>
                        <p className="text-sm text-neutral-500 mb-4">
                          {item.description || "Quality healthcare product"}
                        </p>

                        <div className="flex items-center justify-between">
                          <div className="flex items-center space-x-3">
                            <motion.button
                              onClick={() =>
                                updateCartItemQuantity(
                                  item.id,
                                  item.quantity - 1
                                )
                              }
                              className="w-8 h-8 rounded-full bg-neutral-100 hover:bg-primary-100 flex items-center justify-center transition-colors duration-300"
                              whileHover={{ scale: 1.1 }}
                              whileTap={{ scale: 0.9 }}
                            >
                              <MinusCircle className="w-4 h-4 text-neutral-600" />
                            </motion.button>
                            <span className="w-12 text-center font-semibold text-neutral-700">
                              {item.quantity}
                            </span>
                            <motion.button
                              onClick={() =>
                                updateCartItemQuantity(
                                  item.id,
                                  item.quantity + 1
                                )
                              }
                              className="w-8 h-8 rounded-full bg-neutral-100 hover:bg-primary-100 flex items-center justify-center transition-colors duration-300"
                              whileHover={{ scale: 1.1 }}
                              whileTap={{ scale: 0.9 }}
                            >
                              <PlusCircle className="w-4 h-4 text-neutral-600" />
                            </motion.button>
                          </div>

                          <div className="text-right">
                            <p className="text-xl font-bold text-primary-600">
                              Ksh{" "}
                              {(
                                parseFloat(item.retailPrice) * item.quantity
                              ).toFixed(2)}
                            </p>
                            <p className="text-sm text-neutral-500">
                              Ksh {item.retailPrice} each
                            </p>
                          </div>
                        </div>
                      </div>

                      <motion.button
                        onClick={() => removeFromCart(item.id)}
                        className="p-2 rounded-xl bg-red-50 hover:bg-red-100 text-red-500 transition-colors duration-300"
                        whileHover={{ scale: 1.1 }}
                        whileTap={{ scale: 0.9 }}
                      >
                        <Trash2 className="w-5 h-5" />
                      </motion.button>
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>

            {/* Order Summary */}
            <motion.div
              className="bg-white rounded-2xl shadow-soft border border-neutral-100 p-8 sticky top-8"
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6, delay: 0.3 }}
            >
              <h2 className="text-2xl font-bold text-neutral-900 mb-6">
                Order Summary
              </h2>

              <div className="space-y-4 mb-6">
                <div className="flex justify-between items-center">
                  <span className="text-neutral-600">
                    Subtotal ({cartItems.length} items)
                  </span>
                  <span className="font-semibold text-neutral-900">
                    Ksh {totalCost.toFixed(2)}
                  </span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-neutral-600">Shipping</span>
                  <span className="font-semibold text-secondary-600">FREE</span>
                </div>
                <div className="border-t border-neutral-200 pt-4">
                  <div className="flex justify-between items-center">
                    <span className="text-lg font-bold text-neutral-900">
                      Total
                    </span>
                    <span className="text-2xl font-bold text-primary-600">
                      Ksh {totalCost.toFixed(2)}
                    </span>
                  </div>
                </div>
              </div>

              {/* Prescription Warning */}
              {hasPrescriptionItems && (
                <div className="bg-orange-50 border border-orange-200 rounded-xl p-4 mb-6">
                  <div className="flex items-start space-x-3">
                    <AlertTriangle className="w-5 h-5 text-orange-500 mt-0.5 flex-shrink-0" />
                    <div>
                      <h4 className="font-semibold text-orange-800 mb-1">
                        Prescription Required
                      </h4>
                      <p className="text-sm text-orange-700">
                        Your cart contains prescription items. You'll need to
                        upload prescriptions during checkout.
                      </p>
                    </div>
                  </div>
                </div>
              )}

              <div className="space-y-3">
                <motion.button
                  onClick={handleCheckout}
                  className="w-full bg-gradient-to-r from-primary-600 to-primary-700 text-white py-4 rounded-xl font-semibold shadow-medium hover:shadow-large transition-all duration-300"
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                >
                  Proceed to Checkout
                </motion.button>
                <motion.button
                  onClick={clearCart}
                  className="w-full bg-red-50 text-red-600 py-3 rounded-xl font-semibold hover:bg-red-100 transition-colors duration-300"
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                >
                  Clear Cart
                </motion.button>
              </div>

              {/* Trust Badges */}
              <div className="mt-8 pt-6 border-t border-neutral-200">
                <div className="grid grid-cols-2 gap-4 text-center">
                  <div className="flex flex-col items-center">
                    <div className="w-8 h-8 bg-secondary-100 rounded-full flex items-center justify-center mb-2">
                      <svg
                        className="w-4 h-4 text-secondary-600"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                        />
                      </svg>
                    </div>
                    <span className="text-xs text-neutral-600">
                      Genuine Products
                    </span>
                  </div>
                  <div className="flex flex-col items-center">
                    <div className="w-8 h-8 bg-primary-100 rounded-full flex items-center justify-center mb-2">
                      <svg
                        className="w-4 h-4 text-primary-600"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M20 7l-8-4-8 4m16 0l-8 4-8-4m16 0v10l-8 4-8-4V7"
                        />
                      </svg>
                    </div>
                    <span className="text-xs text-neutral-600">
                      Fast Delivery
                    </span>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        )}

        {/* Prescription Upload Modal */}
        {showPrescriptionModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <motion.div
              className="bg-white rounded-2xl p-8 max-w-lg w-full max-h-[90vh] overflow-y-auto"
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.3 }}
            >
              <h3 className="text-2xl font-bold text-neutral-900 mb-4">
                Upload Prescriptions
              </h3>
              <p className="text-neutral-600 mb-6">
                Your cart contains prescription items. Please upload the
                required prescriptions to proceed with checkout.
              </p>

              {/* List prescription items */}
              <div className="mb-6">
                <h4 className="font-semibold text-neutral-800 mb-3">
                  Prescription Items:
                </h4>
                <div className="space-y-2">
                  {cartItems
                    .filter(
                      (item) =>
                        item.Prescription === "TRUE" ||
                        item.Prescription === true ||
                        item.Prescription === 1
                    )
                    .map((item, index) => (
                      <div
                        key={index}
                        className="flex items-center justify-between p-3 bg-neutral-50 rounded-lg"
                      >
                        <span className="font-medium text-neutral-700">
                          {item.itemName}
                        </span>
                        <span className="text-sm text-neutral-500">
                          Qty: {item.quantity}
                        </span>
                      </div>
                    ))}
                </div>
              </div>

              <div className="mb-6">
                <label className="block text-sm font-medium text-neutral-700 mb-2">
                  Upload Prescription(s)
                </label>
                <input
                  type="file"
                  accept="image/*,.pdf"
                  multiple
                  className="w-full px-3 py-2 border border-neutral-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                />
                <p className="text-xs text-neutral-500 mt-1">
                  You can upload multiple files. Accepted formats: JPG, PNG, PDF
                </p>
              </div>

              <div className="flex gap-3">
                <button
                  onClick={() => setShowPrescriptionModal(false)}
                  className="flex-1 px-4 py-3 bg-neutral-100 text-neutral-700 rounded-xl font-semibold hover:bg-neutral-200 transition-colors duration-300"
                >
                  Cancel
                </button>
                <button
                  onClick={() => {
                    setShowPrescriptionModal(false);
                    proceedToCheckout();
                  }}
                  className="flex-1 px-4 py-3 bg-gradient-to-r from-primary-600 to-primary-700 text-white rounded-xl font-semibold hover:from-primary-700 hover:to-primary-800 transition-all duration-300"
                >
                  Proceed to Checkout
                </button>
              </div>
            </motion.div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ViewCart;
